// SIMO-SELF-KNOWLEDGE-UI-1: strukturierte Karten für die Self-Knowledge-/
// Error-Payloads. Mobile-first (flex-col, break-words, keine Fixbreiten),
// Tailwind-Utilities konsistent zum bestehenden Card-/Badge-Pattern.
import type {
  ArmedGateBlockedPayload,
  AuditRecentPayload,
  PermissionDeniedPayload,
  PermissionListPayload,
  ReasonCodeErrorPayload,
  SelfKnowledgePayload,
  WhoamiPayload,
} from "../selfKnowledgePayloads";

function modeLabel(mode: string): string {
  switch (mode) {
    case "off":
      return "Aus";
    case "draft":
      return "Draft";
    case "suggest":
      return "Suggest+Go";
    case "auto":
      return "Auto";
    default:
      return mode;
  }
}

function Badge({ text, tone = "neutral" }: { text: string; tone?: "neutral" | "ok" | "warn" | "error" }): JSX.Element {
  const cls =
    tone === "ok"
      ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
      : tone === "warn"
        ? "border-amber-500/40 text-amber-300 bg-amber-500/10"
        : tone === "error"
          ? "border-rose-500/40 text-rose-300 bg-rose-500/10"
          : "border-jarvis-border text-jarvis-text/80 bg-white/5";
  return (
    <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium ${cls}`}>
      {text}
    </span>
  );
}

function CardShell({
  tone,
  title,
  children,
}: {
  tone: "error" | "warn" | "info";
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  const border =
    tone === "error" ? "border-rose-500/40 bg-rose-500/5" : tone === "warn" ? "border-amber-500/40 bg-amber-500/5" : "border-sky-500/40 bg-sky-500/5";
  return (
    <div className={`rounded-lg border ${border} p-2.5 text-sm`} data-testid="self-knowledge-card">
      <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-jarvis-text/70">{title}</div>
      <div className="flex flex-col gap-1.5 break-words">{children}</div>
    </div>
  );
}

function SettingsLink({ url, label }: { url: string; label: string }): JSX.Element {
  return (
    <a href={url} className="text-sky-300 underline underline-offset-2 hover:text-sky-200">
      {label}
    </a>
  );
}

export function PermissionDeniedCard({ data }: { data: PermissionDeniedPayload }): JSX.Element {
  return (
    <CardShell tone="error" title="Berechtigung blockiert">
      <div className="font-medium text-jarvis-text">{data.human_label}</div>
      <div className="flex flex-wrap items-center gap-1">
        <Badge text={`aktuell: ${modeLabel(data.current_mode)}`} tone="error" />
        <Badge text={`default: ${modeLabel(data.default_mode)}`} />
        <code className="text-[10px] text-jarvis-text/60">{data.permission_key}</code>
      </div>
      <div className="text-jarvis-text/80">{data.fix_hint}</div>
      <SettingsLink url={data.settings_url} label="Zu den Berechtigungen" />
    </CardShell>
  );
}

export function ArmedGateBlockedCard({ data }: { data: ArmedGateBlockedPayload }): JSX.Element {
  return (
    <CardShell tone="warn" title="Aktion nicht scharfgeschaltet">
      <div className="font-medium text-jarvis-text">{data.human_label}</div>
      <div className="flex flex-wrap items-center gap-1">
        <Badge text={data.reason === "armed_expired" ? "abgelaufen" : "nicht scharf"} tone="warn" />
        <Badge text={`Master: ${modeLabel(data.arm_permission_mode)}`} tone={data.arm_permission_mode === "off" ? "error" : "neutral"} />
        <code className="text-[10px] text-jarvis-text/60">{data.permission_key}</code>
      </div>
      <div className="text-jarvis-text/80">{data.fix_hint}</div>
      <SettingsLink url={data.command_center_url} label="Zum Command-Center" />
    </CardShell>
  );
}

export function WhoamiCard({ data }: { data: WhoamiPayload }): JSX.Element {
  const rows: Array<[string, string]> = [
    ["Host", data.runtime_host + (data.network ? ` (${data.network})` : "")],
    ["Agent-Runtime", data.agent_runtime],
    ["Commit", data.commit ?? "—"],
    ["Branch", data.branch ?? "—"],
    ["Source", data.source_path],
    ["Memory-Einträge", String(data.memory_count)],
    ["Audit-Einträge", String(data.audit_count)],
  ];
  return (
    <CardShell tone="info" title="Runtime-Selbstauskunft">
      <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-jarvis-text/60">{k}</dt>
            <dd className="text-jarvis-text/90 break-all">{v}</dd>
          </div>
        ))}
      </dl>
    </CardShell>
  );
}

export function PermissionListCard({ data }: { data: PermissionListPayload }): JSX.Element {
  return (
    <CardShell tone="info" title={`Berechtigungen (${data.count})`}>
      <ul className="flex max-h-64 flex-col gap-1 overflow-y-auto">
        {data.permissions.map((p) => (
          <li key={p.id} className="flex items-center justify-between gap-2">
            <span className="truncate text-jarvis-text/90" title={p.description}>
              {p.label}
            </span>
            <Badge text={modeLabel(p.current_mode)} tone={p.allowed ? "ok" : "error"} />
          </li>
        ))}
      </ul>
      <SettingsLink url={data.settings_url} label="Berechtigungen verwalten" />
    </CardShell>
  );
}

export function AuditRecentCard({ data }: { data: AuditRecentPayload }): JSX.Element {
  return (
    <CardShell tone="info" title={`Letzte Aktionen (${data.count}/${data.scanned})`}>
      <ul className="flex max-h-64 flex-col gap-1 overflow-y-auto">
        {data.entries.map((e) => (
          <li key={e.id} className="flex items-start justify-between gap-2">
            <span className="min-w-0 flex-1 truncate text-jarvis-text/90" title={e.summary}>
              {e.summary}
            </span>
            <Badge text={e.result} tone={e.result === "ok" ? "ok" : e.result === "blocked" || e.result === "error" ? "error" : "neutral"} />
          </li>
        ))}
      </ul>
    </CardShell>
  );
}

export function ReasonCodeErrorCard({ data }: { data: ReasonCodeErrorPayload }): JSX.Element {
  return (
    <CardShell tone="error" title="Aktion blockiert">
      <div className="text-jarvis-text/90">{data.error}</div>
      <div className="flex flex-wrap items-center gap-1">
        <Badge text={data.reason_code} tone="error" />
        {typeof data.current_count === "number" && typeof data.max === "number" ? (
          <Badge text={`${data.current_count}/${data.max}`} tone="warn" />
        ) : null}
      </div>
      {data.cleanup_hint ? <div className="text-jarvis-text/80">{data.cleanup_hint}</div> : null}
    </CardShell>
  );
}

/** Zentrale Switch-Komponente: rendert die passende Karte für einen Payload. */
export function SelfKnowledgeCard({ payload }: { payload: SelfKnowledgePayload }): JSX.Element {
  switch (payload.kind) {
    case "permission_denied":
      return <PermissionDeniedCard data={payload.data} />;
    case "armed_gate_blocked":
      return <ArmedGateBlockedCard data={payload.data} />;
    case "whoami":
      return <WhoamiCard data={payload.data} />;
    case "permission_list":
      return <PermissionListCard data={payload.data} />;
    case "audit_recent":
      return <AuditRecentCard data={payload.data} />;
    case "reason_code_error":
      return <ReasonCodeErrorCard data={payload.data} />;
  }
}
